import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  useMediaQuery,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Skeleton,
  Container,
  Collapse,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const Receivers = () => {
  const { accessToken, isAdmin, isSuperuser } = useAuth();
  const navigate = useNavigate();
  const [allReceivers, setAllReceivers] = useState([]);
  const [receivers, setReceivers] = useState([]);
  const [sites, setSites] = useState(new Map());
  const [sensorData, setSensorData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isReceiversOpen, setIsReceiversOpen] = useState(true);
  const [filters, setFilters] = useState({
    device_id: "",
    name: "",
    site_id: "",
    site_name: "",
    device_type: "",
  });
  const [selectedReceiver, setSelectedReceiver] = useState("");
  const [timeRange, setTimeRange] = useState("7d");

  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const baseURL = process.env.REACT_APP_BASEURL;

  const metricColors = {
    voltage_battery: {
      backgroundColor: "rgba(25, 118, 210, 0.1)",
      borderColor: "rgba(25, 118, 210, 1)",
      pointBackgroundColor: "rgba(25, 118, 210, 1)",
    },
    voltage_external: {
      backgroundColor: "rgba(220, 53, 69, 0.1)",
      borderColor: "rgba(220, 53, 69, 1)",
      pointBackgroundColor: "rgba(220, 53, 69, 1)",
    },
  };

  const metricUnits = {
    voltage_battery: "V",
    voltage_external: "V",
  };

  const metricRanges = {
    voltage_battery: { min: 3.4, max: 4.2 },
    voltage_external: { min: 0, max: 7.5 },
  };

  // Fetch receivers, sites on mount
  useEffect(() => {
    const fetchReceivers = async () => {
      setLoading(true);
      setError(null);
      try {
        const deviceResponse = await axios.get(`${baseURL}/devices`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: { with_details: true },
        });
        const fetchedDevices = deviceResponse.data || [];
        const filteredReceivers = fetchedDevices.filter((device) =>
          ["base", "echo"].includes(device.device_type?.toLowerCase())
        );
        setAllReceivers(filteredReceivers);
        setReceivers(filteredReceivers);
        setSelectedReceiver(
          filteredReceivers.length > 0 ? String(filteredReceivers[0].id) : ""
        );

        const sitesResponse = await axios.get(`${baseURL}/get-sites`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const fetchedSites = sitesResponse.data.data || [];
        setSites(
          new Map(
            fetchedSites.map((site) => [
              String(site.id),
              site.name || `Site ${site.id}`,
            ])
          )
        );

      } catch (error) {
        console.error(
          "Receivers fetch error:",
          error.response ? error.response.data : error.message
        );
        setError("Failed to load receivers. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchReceivers();
  }, [accessToken]);

  // Set initial filter values after sites is fetched
  useEffect(() => {
    if (sites.size > 0 && filters.site_name === "") {
      const sortedSites = [...sites.entries()].sort((a, b) => {
        const nameA = a[1] || `Site ${a[0]}`;
        const nameB = b[1] || `Site ${b[0]}`;
        return nameA.localeCompare(nameB) || a[0] - b[0];
      });
      const [firstSiteId, firstSiteName] = sortedSites[0];
      setFilters((prev) => ({
        ...prev,
        site_id: firstSiteId,
        site_name: firstSiteName,
      }));
    }
  }, [sites]);

  // Filter receivers locally when filters change
  useEffect(() => {
    const filteredReceivers = allReceivers.filter((device) => {
      const deviceIdMatch = filters.device_id
        ? String(device.id) === String(filters.device_id)
        : true;
      const nameMatch = filters.name
        ? device.name?.toLowerCase().includes(filters.name.toLowerCase())
        : true;
      const siteIdMatch = filters.site_id
        ? String(device.site) === String(filters.site_id)
        : true;
      const siteNameMatch = filters.site_name
        ? (sites.get(String(device.site)) || `Site ${device.site}`)
            .toLowerCase()
            .includes(filters.site_name.toLowerCase())
        : true;
      const deviceTypeMatch = filters.device_type
        ? device.device_type?.toLowerCase() ===
          filters.device_type.toLowerCase()
        : true;

      return (
        deviceIdMatch &&
        nameMatch &&
        siteIdMatch &&
        siteNameMatch &&
        deviceTypeMatch
      );
    });

    setReceivers(filteredReceivers);
    // Update selectedReceiver if it's no longer in filtered receivers
    if (
      selectedReceiver &&
      !filteredReceivers.some((r) => String(r.id) === String(selectedReceiver))
    ) {
      setSelectedReceiver(
        filteredReceivers.length > 0 ? String(filteredReceivers[0].id) : ""
      );
    }
  }, [filters, allReceivers, sites, selectedReceiver]);

  // Fetch sensor data for selected receiver and time range
  useEffect(() => {
    if (!selectedReceiver) return;

    const fetchSensorData = async () => {
      try {
        const endDate = new Date().toISOString();
        const daysToSubtract =
          timeRange === "30d" ? 30 : timeRange === "14d" ? 14 : 7;
        const startDate = new Date(
          new Date().setDate(new Date().getDate() - daysToSubtract)
        ).toISOString();

        const response = await axios.get(`${baseURL}/sensor_data/aggregated`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: {
            device: selectedReceiver,
            since: startDate,
            until: endDate,
            window_size: 500,
          },
        });
        setSensorData(response.data || []);
      } catch (error) {
        console.error(
          "Sensor data fetch error:",
          error.response ? error.response.data : error.message
        );
        setSensorData([]);
      }
    };

    fetchSensorData();
  }, [accessToken, selectedReceiver, timeRange]);

  // Handle filter changes and synchronize Site ID, Site Name, and Device ID
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => {
      const newFilters = { ...prev, [name]: value };

      if (name === "site_id" && value) {
        const siteName = sites.get(String(value)) || `Site ${value}`;
        newFilters.site_name = siteName;
        // Reset device_id if they are not valid for the new site
        if (newFilters.device_id) {
          const selectedReceiver = allReceivers.find(
            (receiver) =>
              String(receiver.id) === String(newFilters.device_id) &&
              String(receiver.site) === String(value)
          );
          newFilters.device_id = selectedReceiver ? newFilters.device_id : "";
        }
      } else if (name === "site_id" && !value) {
        newFilters.site_name = "";
        newFilters.device_id = "";
      } else if (name === "site_name" && value) {
        const siteEntry = [...sites.entries()].find(
          ([id, name]) => name === value
        );
        newFilters.site_id = siteEntry ? String(siteEntry[0]) : "";
        // Reset device_id if they are not valid for the new site
        if (newFilters.device_id && newFilters.site_id) {
          const selectedReceiver = allReceivers.find(
            (receiver) =>
              String(receiver.id) === String(newFilters.device_id) &&
              String(receiver.site) === String(newFilters.site_id)
          );
          newFilters.device_id = selectedReceiver ? newFilters.device_id : "";
        }
      } else if (name === "site_name" && !value) {
        newFilters.site_id = "";
        newFilters.device_id = "";
      }

      return newFilters;
    });
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
  };

  const handleClearFilters = () => {
    setFilters({
      device_id: "",
      name: "",
      site_id: "",
      site_name: "",
      device_type: "",
    });
  };

  const handleReceiverChange = (e) => {
    setSelectedReceiver(e.target.value);
  };

  const handleTimeRangeChange = (e, newValue) => {
    if (newValue !== null) {
      setTimeRange(newValue);
    }
  };

  const getChartData = (data, metric) => {
    if (!data || !Array.isArray(data)) {
      return {
        labels: [],
        datasets: [],
      };
    }

    const labels = data.map((item) => {
      const medianDate = new Date(item.timestamp.median);
      return medianDate.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        ...(timeRange === "30d"
          ? {}
          : { weekday: isXsScreen ? undefined : "short" }),
      });
    });

    const timestamps = data.map((item) => item.timestamp.median);

    const values = data.map((item) => {
      const meanValue =
        item[metric]?.mean ??
        (metricRanges[metric].min + metricRanges[metric].max) / 2;
      return meanValue;
    });

    return {
      labels,
      datasets: [
        {
          label: `${
            metric.charAt(0).toUpperCase() + metric.slice(1).replace("_", " ")
          }`,
          data: values,
          timestamps,
          fill: true,
          backgroundColor: metricColors[metric].backgroundColor,
          borderColor: metricColors[metric].borderColor,
          pointBackgroundColor: metricColors[metric].pointBackgroundColor,
          borderWidth: 2,
          tension: 0.4,
          pointRadius: isSmallScreen ? 0 : data.length > 50 ? 0 : 3,
          pointHoverRadius: 6,
        },
      ],
    };
  };

  const getChartOptions = (metric) => {
    const stepSizes = {
      voltage_battery: 0.2,
      voltage_external: 2.5,
    };

    const fontSizeBase = isXsScreen ? 7 : isSmallScreen ? 8 : 10;
    const maxTicksLimit = isXsScreen
      ? 4
      : isSmallScreen
      ? 5
      : timeRange === "30d"
      ? 10
      : timeRange === "14d"
      ? 7
      : 5;

    return {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          title: {
            display: !isXsScreen,
            text: "Date",
            font: { size: fontSizeBase + 2 },
          },
          grid: { display: false },
          ticks: {
            maxRotation: isXsScreen ? 90 : isSmallScreen ? 45 : 30,
            minRotation: 0,
            font: { size: fontSizeBase },
            autoSkip: true,
            maxTicksLimit: maxTicksLimit,
          },
        },
        y: {
          title: {
            display: !isXsScreen,
            text: `${
              metric.charAt(0).toUpperCase() + metric.slice(1).replace("_", " ")
            } (${metricUnits[metric]})`,
            font: { size: fontSizeBase + 2 },
          },
          min: metricRanges[metric].min,
          max: metricRanges[metric].max,
          ticks: {
            stepSize: stepSizes[metric],
            font: { size: fontSizeBase },
          },
        },
      },
      plugins: {
        legend: {
          display: false,
          labels: {
            font: { size: fontSizeBase + 1 },
          },
        },
        tooltip: {
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          titleFont: { size: fontSizeBase + 4 },
          bodyFont: { size: fontSizeBase + 3 },
          padding: isXsScreen ? 8 : 12,
          cornerRadius: 4,
          callbacks: {
            title: (tooltipItems) => {
              const index = tooltipItems[0].dataIndex;
              const timestamp = tooltipItems[0].dataset.timestamps[index];
              const date = new Date(timestamp);
              return date.toLocaleString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              });
            },
            label: (context) => {
              const value = context.parsed.y.toFixed(2);
              return `${context.dataset.label}: ${value} ${metricUnits[metric]}`;
            },
          },
        },
      },
      interaction: { mode: "index", intersect: false },
      elements: { line: { borderJoinStyle: "round" } },
    };
  };

  const LoadingSkeleton = () => (
    <Container maxWidth="xl" disableGutters sx={{ p: 0 }}>
      <Box sx={{ p: 0 }}>
        <Typography variant="h4" gutterBottom>
          <Skeleton width="30%" height={40} />
        </Typography>
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              mb: 2,
            }}
          >
            {Array(5)
              .fill()
              .map((_, index) => (
                <Skeleton
                  key={index}
                  variant="rectangular"
                  width={isXsScreen ? "100%" : 150}
                  height={56}
                />
              ))}
          </Box>
          <Skeleton
            variant="rectangular"
            width={isXsScreen ? "100%" : 120}
            height={40}
          />
        </Box>
        <Box
          sx={{
            mb: 3,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Skeleton
            variant="rectangular"
            width={isXsScreen ? "100%" : 200}
            height={40}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <Skeleton width={80} height={24} sx={{ mr: 2 }} />
            <Skeleton
              variant="rectangular"
              width={isXsScreen ? 150 : 200}
              height={40}
            />
          </Box>
        </Box>
        <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            <Skeleton width="20%" height={32} />
          </Typography>
          <Box sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: { xs: 300, sm: 1000 } }}>
              <TableHead>
                <TableRow>
                  {[
                    "ID",
                    "Serial Number",
                    "Name",
                    "Device Type",
                    "Site",
                    "Status",
                    "Last Seen",
                    "Packets/Day",
                  ].map((header) => (
                    <TableCell key={header}>
                      <Skeleton />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Array(3)
                  .fill()
                  .map((_, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {Array(9)
                        .fill()
                        .map((_, cellIndex) => (
                          <TableCell key={cellIndex}>
                            <Skeleton />
                          </TableCell>
                        ))}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </Box>
        </Paper>
        <Paper sx={{ p: { xs: 1, sm: 2 } }}>
          <Typography variant="h6" gutterBottom>
            <Skeleton width="30%" height={32} />
          </Typography>
          {Array(2)
            .fill()
            .map((_, index) => (
              <Box key={index} sx={{ mt: 2 }}>
                <Skeleton
                  variant="rectangular"
                  height={isXsScreen ? 200 : 300}
                />
              </Box>
            ))}
        </Paper>
      </Box>
    </Container>
  );  

  if (loading) return <LoadingSkeleton />;

  if (error)
    return (
      <Container maxWidth="xl">
        <Box sx={{ p: { xs: 1, sm: 2 } }}>
          <Typography color="error">{error}</Typography>
        </Box>
      </Container>
    );

  const filteredReceiverIds = filters.site_id
    ? allReceivers
        .filter((receiver) => String(receiver.site) === String(filters.site_id))
        .map((receiver) => String(receiver.id))
    : [...new Set(allReceivers.map((receiver) => String(receiver.id)))];
  const uniqueReceiverIds = [...new Set(filteredReceiverIds)].sort();

  return (
    <Container maxWidth="xl" disableGutters sx={{ p: 0 }}>
      <Box sx={{ p: 0 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography
            variant="h4"
            gutterBottom
            sx={{ fontSize: { xs: "1.75rem", md: "2.125rem" } }}
          >
            Receivers
          </Typography>
          {(isAdmin || isSuperuser) && (
            <Box sx={{ display: "flex", justifyContent: "end" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate("/receivers/add")}
                sx={{
                  fontSize: { xs: "0.9rem", md: "1rem" },
                  textTransform: "none",
                }}
              >
                Add Receiver
              </Button>
            </Box>
          )}
        </Box>

        <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 4 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontSize: { xs: "1.1rem", md: "1.25rem" } }}
          >
            Filter Receivers
          </Typography>
          <Box
            component="form"
            onSubmit={handleFilterSubmit}
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              mb: 2,
            }}
          >
            <TextField
              label="Receiver Name"
              name="name"
              value={filters.name}
              onChange={handleFilterChange}
              variant="outlined"
              size="small"
              sx={{ minWidth: { xs: "100%", sm: 150 } }}
              InputLabelProps={{ style: { color: "black", fontWeight: 100 } }}
            />
            <FormControl
              variant="outlined"
              size="small"
              sx={{ minWidth: { xs: "100%", sm: 150 } }}
            >
              <InputLabel
                id="site-name-label"
                sx={{ color: "black", fontWeight: 100 }}
              >
                Site Name
              </InputLabel>
              <Select
                labelId="site-name-label"
                name="site_name"
                value={filters.site_name}
                onChange={handleFilterChange}
                label="Site Name"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {[...new Set(sites.values())].sort().map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              variant="outlined"
              size="small"
              sx={{ minWidth: { xs: "100%", sm: 150 } }}
            >
              <InputLabel
                id="site-id-label"
                sx={{ color: "black", fontWeight: 100 }}
              >
                Site ID
              </InputLabel>
              <Select
                labelId="site-id-label"
                name="site_id"
                value={filters.site_id}
                onChange={handleFilterChange}
                label="Site ID"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {[...sites.keys()].sort().map((id) => (
                  <MenuItem key={id} value={id}>
                    {id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              variant="outlined"
              size="small"
              sx={{ minWidth: { xs: "100%", sm: 150 } }}
            >
              <InputLabel
                id="device-id-label"
                sx={{ color: "black", fontWeight: 100 }}
              >
                Device ID
              </InputLabel>
              <Select
                labelId="device-id-label"
                name="device_id"
                value={filters.device_id}
                onChange={handleFilterChange}
                label="Device ID"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {uniqueReceiverIds.map((id) => (
                  <MenuItem key={id} value={id}>
                    {id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              variant="outlined"
              size="small"
              sx={{ minWidth: { xs: "100%", sm: 150 } }}
            >
              <InputLabel
                id="device-type-label"
                sx={{ color: "black", fontWeight: 100 }}
              >
                Device Type
              </InputLabel>
              <Select
                labelId="device-type-label"
                name="device_type"
                value={filters.device_type}
                onChange={handleFilterChange}
                label="Device Type"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                <MenuItem value="base">Base</MenuItem>
                <MenuItem value="echo">Echo</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleClearFilters}
            >
              Clear Filters
            </Button>
          </Box>
        </Paper>

        <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              p: 1,
              borderRadius: 1,
            }}
            onClick={() => setIsReceiversOpen(!isReceiversOpen)}
          >
            <Typography
              variant="h6"
              sx={{ fontSize: { xs: "1.1rem", md: "1.25rem" }, flexGrow: 1 }}
            >
              Receivers
            </Typography>
            <IconButton size="small">
              {isReceiversOpen ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          <Collapse in={isReceiversOpen}>
            {receivers.length > 0 ? (
              <Box sx={{ overflowX: "auto", mt: 2 }}>
                <Table sx={{ minWidth: { xs: 300, sm: 1000 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Receiver Type</TableCell>
                      <TableCell>Latitude</TableCell>
                      <TableCell>Longitude</TableCell>
                      <TableCell>Height</TableCell>
                      <TableCell>Antenna Type</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {receivers.map((receiver) => (
                      <TableRow key={receiver.id}>
                        <TableCell>{receiver.id}</TableCell>
                        <TableCell>{receiver.name}</TableCell>
                        <TableCell>{receiver.device_type}</TableCell>
                        <TableCell>{receiver.location?.latitude ?? "N/A"}</TableCell>
                        <TableCell>{receiver.location?.longitude ?? "N/A"}</TableCell>
                        <TableCell>{receiver.location?.height ?? "N/A"}</TableCell>
                        <TableCell>{receiver.antenna_type || "N/A"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ) : (
              <Typography variant="body2" sx={{ mt: 2 }}>
                No receivers found matching the filters.
              </Typography>
            )}
          </Collapse>
        </Paper>

        <Box
          sx={{
            mb: 3,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
          }}
        >
          <FormControl
            variant="outlined"
            sx={{ minWidth: { xs: "100%", sm: 200 } }}
          >
            <InputLabel id="receiver-select-label" sx={{ color: "black" }}>
              Receiver
            </InputLabel>
            <Select
              labelId="receiver-select-label"
              id="receiver-select"
              value={selectedReceiver}
              onChange={handleReceiverChange}
              label="Receiver"
              sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
            >
              {receivers.map((receiver) => (
                <MenuItem
                  key={receiver.id}
                  value={receiver.id}
                  sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
                >
                  {receiver.name || `Receiver ${receiver.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: { xs: "100%", sm: "auto" },
              justifyContent: { xs: "space-between", sm: "flex-end" },
            }}
          >
            <Typography
              variant="body2"
              sx={{ mr: 2, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
            >
              Time Range:
            </Typography>
            <ToggleButtonGroup
              value={timeRange}
              exclusive
              onChange={handleTimeRangeChange}
              size={isXsScreen ? "small" : "medium"}
              aria-label="time range"
              sx={{
                "& .MuiToggleButton-root": {
                  px: { xs: 1, sm: 2 },
                  py: { xs: 0.5, sm: 0.5 },
                  fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.875rem" },
                },
              }}
            >
              <ToggleButton value="7d" aria-label="7 days">
                7 Days
              </ToggleButton>
              <ToggleButton value="14d" aria-label="14 days">
                14 Days
              </ToggleButton>
              <ToggleButton value="30d" aria-label="30 days">
                30 Days
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
        {selectedReceiver && (
          <Paper sx={{ p: { xs: 1, sm: 2 } }}>
            <Typography
              variant="h6"
              sx={{ fontSize: { xs: "1.1rem", md: "1.25rem" }, mb: 2 }}
            >
              {receivers.find((r) => r.id === selectedReceiver)?.name ||
                `Receiver ${selectedReceiver}`}{" "}
              Voltage Data
            </Typography>
            <Box sx={{ mt: { xs: 2, sm: 3 } }}>
              {["voltage_battery", "voltage_external"].map((metric) => (
                <Paper
                  key={`${metric}-chart`}
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    mb: { xs: 2, sm: 3 },
                    borderRadius: 2,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    transition: "box-shadow 0.3s ease",
                    "&:hover": {
                      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                    },
                  }}
                >
                  <Typography
                    variant="h6"
                    fontWeight="500"
                    color={metricColors[metric].borderColor}
                    sx={{
                      mb: { xs: 1, sm: 2 },
                      fontSize: { xs: "0.95rem", sm: "1.1rem", md: "1.25rem" },
                    }}
                  >
                    {metric === "voltage_battery" ? "Battery" : "Input Voltage"}
                  </Typography>
                  <Box
                    sx={{
                      height: { xs: 200, sm: 250, md: 300, lg: 350 },
                      width: "100%",
                    }}
                  >
                    <Line
                      data={getChartData(sensorData, metric)}
                      options={getChartOptions(metric)}
                    />
                  </Box>
                </Paper>
              ))}
            </Box>
          </Paper>
        )}
      </Box>
    </Container>
  );
};

export default Receivers;
